"use client";

import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  ProposalActionFailedError,
  useProposalBatch,
  useProposalMutations,
} from "@stockline/web";
import type { UUID } from "@/app/api/types";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useUser } from "@/components/auth-provider";
import { preOrderKeys } from "@/query/hooks/usePreOrders/keys";
import { flowKeys } from "@/query/keys/flows";
import { whatsappKeys } from "@/query/keys/whatsapp";
import { smsKeys } from "@/query/hooks/useSMS";

/**
 * Wraps useProposalMutations approve/reject so callers don't have to
 * duplicate error handling, the `acting` flag, and TanStack Query
 * invalidation. After approval the proposal-batches Dexie table is reactive
 * (so the virtual proposal disappears immediately) but the executed
 * pre-order/flows live in TanStack Query — invalidate them here so the
 * recap surfaces the real entity in the same render pass.
 */
export function useProposalApprovalActions(batchId: string) {
  const { t } = useTranslation();
  const user = useUser();
  const queryClient = useQueryClient();
  const { approveProposal, rejectProposal } = useProposalMutations();
  const [acting, setActing] = useState(false);
  // Read the batch reactively so we can target the right channel's
  // messages query for invalidation post-approval. The batch is in Dexie
  // by the time the user clicks approve; null is a no-op.
  const batch = useProposalBatch(batchId);

  const invalidateExecutedEntities = useCallback(async () => {
    const channelInvalidations: Promise<unknown>[] = [];
    if (batch?.sourceChatId) {
      // WhatsApp/SMS messages embed pre-order metadata, so the conversation
      // must re-fetch to surface the freshly created pre-order. Email
      // pre-orders are reached via preOrderKeys (already invalidated below).
      if (batch.sourceChannelType === "whatsapp") {
        channelInvalidations.push(
          queryClient.invalidateQueries({
            queryKey: whatsappKeys.messages(batch.sourceChatId as UUID),
          }),
        );
      } else if (batch.sourceChannelType === "sms") {
        channelInvalidations.push(
          queryClient.invalidateQueries({
            queryKey: smsKeys.messages(batch.sourceChatId as UUID),
          }),
        );
      }
    }
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: preOrderKeys.all }),
      queryClient.invalidateQueries({ queryKey: flowKeys.all }),
      ...channelInvalidations,
    ]);
  }, [queryClient, batch?.sourceChannelType, batch?.sourceChatId]);

  const handleApprove = useCallback(async () => {
    setActing(true);
    try {
      await approveProposal(batchId, user.id);
      await invalidateExecutedEntities();
    } catch (err) {
      const reason =
        err instanceof ProposalActionFailedError
          ? (err.failureReason ?? t("aiProposals.actionFailed"))
          : t("aiProposals.actionFailed");
      toast.error(reason);
    } finally {
      setActing(false);
    }
  }, [batchId, user.id, approveProposal, invalidateExecutedEntities, t]);

  const handleReject = useCallback(async () => {
    setActing(true);
    try {
      await rejectProposal(batchId, user.id);
    } catch (err) {
      const reason =
        err instanceof ProposalActionFailedError
          ? (err.failureReason ?? t("aiProposals.actionFailed"))
          : t("aiProposals.actionFailed");
      toast.error(reason);
    } finally {
      setActing(false);
    }
  }, [batchId, user.id, rejectProposal, t]);

  return { acting, handleApprove, handleReject };
}
